import React, { useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../../../libs/utils";

const clampPage = (value, totalPages) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num < 1) return 1;
  return Math.min(Math.floor(num), totalPages);
};

const getPageItems = (currentPage, totalPages) => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }
  const visible = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);
  const pages = Array.from(visible)
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((a, b) => a - b);

  return pages.reduce((items, page, index) => {
    const prev = pages[index - 1];
    if (prev && page - prev > 1) items.push(`ellipsis-${prev}`);
    items.push(page);
    return items;
  }, []);
};

const BatchPaginationBar = ({ page, totalRecords, rowsPerPage, onPageChange, className }) => {
  const totalPages = Math.max(1, Math.ceil(totalRecords / rowsPerPage));
  const currentPage = clampPage(page, totalPages);
  const items = useMemo(() => getPageItems(currentPage, totalPages), [currentPage, totalPages]);

  if (totalPages <= 1) return null;

  return (
    <div className={cn("flex items-center gap-1.5", className)}>
      <button
        type="button"
        aria-label="Previous page"
        disabled={currentPage <= 1}
        onClick={() => onPageChange(currentPage - 1)}
        className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>

      {items.map((item) =>
        typeof item === "number" ? (
          <button
            key={item}
            type="button"
            aria-current={item === currentPage ? "page" : undefined}
            onClick={() => onPageChange(item)}
            className={cn(
              "flex h-9 min-w-9 items-center justify-center rounded-lg px-2 text-sm font-semibold transition-colors",
              item === currentPage
                ? "bg-brand text-white shadow-sm"
                : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            )}
          >
            {item}
          </button>
        ) : (
          <span key={item} className="flex h-9 w-6 items-center justify-center text-sm text-slate-400">
            …
          </span>
        )
      )}

      <button
        type="button"
        aria-label="Next page"
        disabled={currentPage >= totalPages}
        onClick={() => onPageChange(currentPage + 1)}
        className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
};

export default BatchPaginationBar;
